/**
 * CDD (Client Acceptance) → 이후 Stage 문서 자동 연동.
 * Director → Form 45 / Secretary → Form 45B /
 * Corporate SH (IHC) → CS Profile / Controllers → Notices
 * Company name → Form45/45B/Notice/Constitution/Engagement (인물 유무와 무관)
 */
import type { SgIncPreIncludes } from '../../../mappers/mapSgIncorporationToPreValues';
import { bareKey, resolveB04DisplayName } from './useCddFill';

function str(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function maxIndexed(values: Record<string, string>, prefix: string): number {
  let max = 0;
  const re = new RegExp(`^${prefix}(\\d+)_`);
  for (const k of Object.keys(values)) {
    const m = re.exec(k);
    if (!m) continue;
    if (!str(values[k])) continue;
    max = Math.max(max, Number(m[1]) || 0);
  }
  return max;
}

/** 값이 있는 인덱스 id 목록 (1..max 중 이름 키가 있는 것만) */
function namedIds(
  values: Record<string, string>,
  prefix: string,
  nameSuffix: string,
): number[] {
  const max = maxIndexed(values, prefix);
  const out: number[] = [];
  for (let i = 1; i <= max; i++) {
    if (str(values[`${prefix}${i}_${nameSuffix}`])) out.push(i);
  }
  return out;
}

function copyIf(
  values: Record<string, string>,
  setValue: (k: string, v: string) => void,
  fromKey: string,
  toKey: string,
) {
  const v = str(values[fromKey]);
  if (!v) return;
  // 대상이 비어 있거나 CDD 와 다를 때만 덮어씀 — CDD 가 소스 of truth
  if (str(values[toKey]) !== v) setValue(toKey, v);
}

function isScopedCompanyNameKey(key: string): boolean {
  return key.includes('/') && bareKey(key) === 'company_name';
}

function isScopedRegNoKey(key: string): boolean {
  return key.includes('/') && bareKey(key) === 'company_registration_no';
}

function uniqIds(...lists: Array<number[] | undefined>): number[] {
  const s = new Set<number>();
  for (const list of lists) {
    for (const id of list || []) {
      if (Number.isFinite(id) && id > 0) s.add(id);
    }
  }
  return [...s].sort((a, b) => a - b);
}

/**
 * entity_name / company_name / proposed_company_name / DocScope company_name
 * 중 가장 긴 값 (짤린 동기화 복구 + Form45↔CDD 역동기화)
 */
export function resolveSharedCompanyName(values: Record<string, string>): string {
  const candidates = [
    str(values.company_name),
    str(values.entity_name),
    str(values.proposed_company_name),
  ];
  for (const [k, v] of Object.entries(values)) {
    if (isScopedCompanyNameKey(k)) candidates.push(str(v));
  }
  const filled = candidates.filter(Boolean);
  if (!filled.length) return '';
  return filled.reduce((a, b) => (b.length > a.length ? b : a));
}

/** CDD entity_uen ↔ company_registration_no / entity_registration_no */
export function resolveSharedRegNo(values: Record<string, string>): string {
  const candidates = [
    str(values.entity_uen),
    str(values.company_registration_no),
    str(values.entity_registration_no),
  ];
  for (const [k, v] of Object.entries(values)) {
    if (isScopedRegNoKey(k)) candidates.push(str(v));
  }
  const filled = candidates.filter(Boolean);
  if (!filled.length) return '';
  return filled.reduce((a, b) => (b.length > a.length ? b : a));
}

/** B04 공통코드 → 표시명으로 복사 (국적·국가) */
function copyB04LabelIf(
  values: Record<string, string>,
  setValue: (k: string, v: string) => void,
  fromKey: string,
  toKey: string,
) {
  const raw = str(values[fromKey]);
  if (!raw) return;
  const v = resolveB04DisplayName(raw);
  if (str(values[toKey]) !== v) setValue(toKey, v);
  // CAF 쪽에도 코드가 남아 있으면 표시명으로 정규화
  if (raw !== v && str(values[fromKey]) === raw) setValue(fromKey, v);
}

export interface SyncStageLists {
  form45Ids: number[];
  form45bIds: number[];
  csProfileIds: number[];
  corpCtrlIds: number[];
  indCtrlIds: number[];
}

/** PackageDoc 에 실제로 떠 있는 문서 복사본 id (인물 미입력여도 회사명 동기화용) */
export type ActiveStageLists = Partial<SyncStageLists>;

export interface SyncResult {
  lists: SyncStageLists;
  includes: Partial<SgIncPreIncludes>;
}

/**
 * 공유 회사명·UEN → 루트 별칭 + 모든 Form45/45B/Notice/Constitution 스코프.
 * 수동으로 CDD만 채워도 Stage 문서 Name of Company / Company No 가 따라가도록 함.
 */
export function syncSharedCompanyToStages(
  values: Record<string, string>,
  setValue: (k: string, v: string) => void,
  active?: ActiveStageLists,
): string {
  const company = resolveSharedCompanyName(values);
  const regNo = resolveSharedRegNo(values);

  if (str(values.company_name) !== company) setValue('company_name', company);
  if (str(values.entity_name) !== company) setValue('entity_name', company);
  if (str(values.proposed_company_name) !== company) setValue('proposed_company_name', company);

  if (str(values.entity_uen) !== regNo) setValue('entity_uen', regNo);
  if (str(values.company_registration_no) !== regNo) setValue('company_registration_no', regNo);
  if (str(values.entity_registration_no) !== regNo) setValue('entity_registration_no', regNo);

  for (const k of Object.keys(values)) {
    if (isScopedCompanyNameKey(k) && str(values[k]) !== company) setValue(k, company);
    if (isScopedRegNoKey(k) && str(values[k]) !== regNo) setValue(k, regNo);
  }

  for (const id of uniqIds(active?.form45Ids)) {
    const sc = `f45_${id}/`;
    if (str(values[sc + 'company_name']) !== company) setValue(sc + 'company_name', company);
    if (str(values[sc + 'company_registration_no']) !== regNo) {
      setValue(sc + 'company_registration_no', regNo);
    }
  }
  for (const id of uniqIds(active?.form45bIds)) {
    const sc = `f45b_${id}/`;
    if (str(values[sc + 'company_name']) !== company) setValue(sc + 'company_name', company);
    if (str(values[sc + 'company_registration_no']) !== regNo) {
      setValue(sc + 'company_registration_no', regNo);
    }
  }
  for (const id of uniqIds(active?.indCtrlIds)) {
    const sc = `ctrli_${id}/`;
    if (str(values[sc + 'company_name']) !== company) setValue(sc + 'company_name', company);
  }
  for (const id of uniqIds(active?.corpCtrlIds)) {
    const sc = `ctrlc_${id}/`;
    if (str(values[sc + 'company_name']) !== company) setValue(sc + 'company_name', company);
  }
  for (const id of uniqIds(active?.csProfileIds)) {
    const sc = `cs_${id}/`;
    // 설립 대상 회사명 (법인주주 회사명 cs_company_name 과 별개)
    if (str(values[sc + 'company_name']) !== company) setValue(sc + 'company_name', company);
    if (str(values[sc + 'company_registration_no']) !== regNo) {
      setValue(sc + 'company_registration_no', regNo);
    }
  }

  return company;
}

/**
 * CAF fill values → stage DocScope 값 복사 + 필요한 문서 복사본 id / include 플래그.
 */
export function syncCafToStageDocs(
  values: Record<string, string>,
  setValue: (k: string, v: string) => void,
  active?: ActiveStageLists,
): SyncResult {
  const includes: Partial<SgIncPreIncludes> = {};
  const company = syncSharedCompanyToStages(values, setValue, active);
  const regNo = resolveSharedRegNo(values);

  // ── Directors → Form 45 ──
  const dirIds = namedIds(values, 'dir', 'director_name');
  if (dirIds.length) includes.directors = true;
  for (const id of dirIds) {
    const sc = `f45_${id}/`;
    if (company) setValue(sc + 'company_name', company);
    if (regNo) setValue(sc + 'company_registration_no', regNo);
    copyIf(values, setValue, `dir${id}_director_name`, sc + 'director_name');
    copyIf(values, setValue, `dir${id}_director_identification_no`, sc + 'director_identification_no');
    copyB04LabelIf(values, setValue, `dir${id}_director_nationality`, sc + 'director_nationality');
    copyIf(values, setValue, `dir${id}_director_address`, sc + 'director_address');
    copyIf(values, setValue, `dir${id}_director_alternate_address`, sc + 'director_alternate_address');
    // Form 45 (공통 최신본) 이사 표: 생년월일·이메일·휴대폰 — CAF Directors 칸에서 가져온다.
    copyIf(values, setValue, `dir${id}_director_date_of_birth`, sc + 'director_date_of_birth');
    copyIf(values, setValue, `dir${id}_director_email`, sc + 'director_email');
    copyIf(values, setValue, `dir${id}_director_mobile_no`, sc + 'director_mobile_no');
    for (const suffix of [
      'has_pre_1993_history',
      'is_employment_pass_holder',
      'needs_interpreter',
      'uses_passport',
      'interpreter_language',
      'interpreter_name',
      'interpreter_nric',
      'appointment_effective_date',
      'execution_day',
      'execution_month',
      'execution_year',
      'execution_date',
    ]) {
      copyIf(values, setValue, `dir${id}_${suffix}`, sc + suffix);
    }
  }

  // ── Secretary → Form 45B ──
  const secIds = namedIds(values, 'sec', 'secretary_name');
  if (secIds.length) includes.secretary = true;
  for (const id of secIds) {
    const sc = `f45b_${id}/`;
    if (company) setValue(sc + 'company_name', company);
    if (regNo) setValue(sc + 'company_registration_no', regNo);
    copyIf(values, setValue, `sec${id}_secretary_name`, sc + 'secretary_name');
    copyIf(values, setValue, `sec${id}_secretary_identification_no`, sc + 'secretary_identification_no');
    copyB04LabelIf(values, setValue, `sec${id}_secretary_nationality`, sc + 'secretary_nationality');
    copyIf(values, setValue, `sec${id}_secretary_address`, sc + 'secretary_address');
  }

  // ── Corporate Shareholder → CS Profile ──
  //   원본 CSP 문서는 법인주주 "본인" 프로필이다(Corporate_Shareholder_* : 회사정보·연락처·
  //   그 회사의 이사/주주). IHC(Immediate Holding) / UPC(Ultimate Parent) 는 법인주주의 상위
  //   지배구조이며 Client Acceptance Form 의 'SHARES HELD BY CORPORATION' 섹션 소속이다
  //   (ihc_* / upc_* 키로 CAF 에 직접 렌더). → CSP 문서로 만들지 않는다.
  //   ※ 예전에는 ihc_name/upc_name 을 cs_1/·cs_2/ 의 cs_company_name 으로 복사했는데,
  //     T-Link 자동매핑이 같은 cs_{N}/ 슬롯에 채운 실제 법인주주 데이터를 덮어써
  //     "회사명은 IHC, 재무·이사·주주는 법인주주" 로 뒤섞이고, UPC 문서는 채울 원천이 없어
  //     빈 문서가 만들어졌다. 여기서는 실제 법인주주 슬롯만 수집한다.
  const csIds: number[] = [];
  for (const k of Object.keys(values)) {
    const m = /^cs_(\d+)\/cs_company_name$/.exec(k);
    if (!m || !str(values[k])) continue;
    const id = Number(m[1]);
    if (id > 0 && !csIds.includes(id)) csIds.push(id);
  }
  csIds.sort((a, b) => a - b);
  if (csIds.length) includes.shareholders = true;

  // ── Individual Controllers → Notice Ind ──
  const indIds = namedIds(values, 'ictrl', 'controller_full_name');
  if (indIds.length) includes.controllers = true;
  for (const id of indIds) {
    const sc = `ctrli_${id}/`;
    if (company) setValue(sc + 'company_name', company);
    copyIf(values, setValue, `ictrl${id}_controller_full_name`, sc + 'controller_full_name');
    copyIf(values, setValue, `ictrl${id}_controller_full_name`, sc + 'notice_recipient_name');
    copyIf(values, setValue, `ictrl${id}_controller_identification_no`, sc + 'controller_identification_no');
    copyB04LabelIf(values, setValue, `ictrl${id}_controller_nationality`, sc + 'controller_nationality');
    copyIf(values, setValue, `ictrl${id}_controller_residential_address`, sc + 'controller_residential_address');
    copyIf(values, setValue, `ictrl${id}_controller_date_of_birth`, sc + 'controller_date_of_birth');
    copyIf(values, setValue, `ictrl${id}_controller_email`, sc + 'controller_email');
    copyIf(values, setValue, `ictrl${id}_controller_contact_no`, sc + 'controller_contact_no');
  }

  // ── Corporate Controllers → Notice Corp ──
  const corpIds = namedIds(values, 'cctrl', 'controller_corporate_name');
  if (corpIds.length) includes.controllers = true;
  for (const id of corpIds) {
    const sc = `ctrlc_${id}/`;
    if (company) setValue(sc + 'company_name', company);
    copyIf(values, setValue, `cctrl${id}_controller_corporate_name`, sc + 'controller_corporate_name');
    copyIf(values, setValue, `cctrl${id}_controller_corporate_name`, sc + 'notice_recipient_name');
    copyIf(values, setValue, `cctrl${id}_controller_uen`, sc + 'controller_uen');
    copyIf(values, setValue, `cctrl${id}_controller_registered_office`, sc + 'controller_registered_office');
    copyIf(values, setValue, `cctrl${id}_controller_email`, sc + 'controller_email');
    copyIf(values, setValue, `cctrl${id}_controller_contact_no`, sc + 'controller_contact_no');
  }

  // 이미 문서에 들어간 B04 코드(B04102 등) → 표시명 정규화
  for (const k of Object.keys(values)) {
    if (!/(nationality|_country|country_of_incorporation)/i.test(k)) continue;
    const raw = str(values[k]);
    if (!/^B\d{5}$/i.test(raw)) continue;
    const label = resolveB04DisplayName(raw);
    if (label && label !== raw) setValue(k, label);
  }

  return {
    lists: {
      form45Ids: dirIds.length ? dirIds : [],
      form45bIds: secIds.length ? secIds : [],
      csProfileIds: csIds,
      indCtrlIds: indIds.length ? indIds : [],
      corpCtrlIds: corpIds.length ? corpIds : [],
    },
    includes,
  };
}
